/**
 * Tailwind className helpers for the VisualDx diagnostic flow.
 *
 * The widget renders inside a chat block and uses semantic design-system
 * tokens (`bg-surface`, `bg-background-secondary`, `border-border`,
 * `text-text-primary`, etc.) so the palette adapts to themes (light /
 * `newBrandDark`) automatically. Originally this file held inline
 * `CSSProperties` helpers with hardcoded hex colors — that was rewritten
 * for PR #9851 review (token compliance + theme awareness).
 *
 * Helpers that depend on per-render state (e.g. `active`) stay as
 * functions returning `cn(...)` strings; static helpers are exported
 * as plain `string`s so consumers can drop them straight into a
 * `className` prop.
 */
import { cn } from '@heidi/utils';

export const cardClass =
  'flex w-full flex-col overflow-hidden rounded-xl bg-background-secondary text-text-primary';

export const progressBarTrackClass = 'relative h-[3px] w-full bg-background-tertiary';

export const progressBarFillClass =
  'h-full bg-background-primary transition-[width] duration-200 ease-out';

export const stepHeadingWrapClass = 'mb-6 mt-6 text-center';

export const bodyClass = 'px-8 pb-2 pt-0';

export const footerClass =
  'flex items-center justify-between gap-3 border-t border-border px-8 py-5';

export const footerRightGroupClass = 'flex gap-3';

export const attributionClass =
  'flex items-center justify-between px-8 pb-4 pt-3 text-text-secondary';

/** Pill / region / age-band button shared across most input steps. */
export const chipClass = (active: boolean): string =>
  cn(
    'cursor-pointer rounded-md border px-3.5 py-2.5 text-center text-sm font-medium transition-colors',
    active
      ? 'border-border-selected bg-surface-selected text-text-active'
      : 'border-border bg-surface text-text-primary hover:bg-surface-hover'
  );

/** Two-up segmented Front/Back tab pill used at the top of the region step. */
export const segmentedWrapClass =
  'mb-3.5 flex gap-0 rounded-md border border-border bg-surface p-[3px]';

export const segmentedButtonClass = (active: boolean): string =>
  cn(
    'flex-1 cursor-pointer rounded border-0 px-4 py-2 text-sm font-semibold transition-colors',
    active
      ? 'bg-background-primary text-text-primary-on-dark'
      : 'bg-transparent text-text-secondary hover:bg-surface-hover'
  );

/** Vertical row card used in the onset step (left icon + text). */
export const rowCardClass = (active: boolean): string =>
  cn(
    'flex w-full cursor-pointer items-center gap-3.5 rounded-[10px] border px-4 py-3 text-left transition-colors',
    active
      ? 'border-border-selected bg-surface-selected'
      : 'border-border bg-surface hover:bg-surface-hover'
  );

export const dlClass = 'mt-3 rounded-lg border border-border bg-surface p-4';

// ── Region step: silhouette + grid layout ─────────────────────────────

export const regionStepLayoutClass = 'flex flex-wrap items-start gap-4';

export const silhouetteWrapClass =
  'flex flex-[0_0_200px] min-w-[180px] justify-center py-1';

export const regionGridWrapClass =
  'grid flex-[1_1_260px] min-w-[240px] grid-cols-3 gap-2.5';

/** Single SVG-region "button" inside the silhouette. */
export const silhouetteRegionClass = (active: boolean): string =>
  cn(
    'cursor-pointer outline-none transition-colors',
    active
      ? 'fill-surface-selected stroke-border-selected [stroke-width:1.6]'
      : 'fill-surface stroke-border [stroke-width:1]'
  );
